import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pagination } from "antd";
import { DownOutlined, SyncOutlined } from "@ant-design/icons";
import {
  getArchivedBrandsList,
  getArchivedBrandStyles,
} from "../../Redux/actions/ArchivedBrands/ArchivedBrands";
import SearchInput from "../../UIComponents/SearchInput/SearchInput";
import DropDown from "../../UIComponents/DropDown/DropDown";
import { Loader } from "../../UIComponents/Loader";
import "./ArchivedBrands.css";

const stylesPageSize = 20;
const chipColors = ["#dfe3d2", "#f7cfae", "#cfe1fb", "#fdf0e4", "#e6dcf5", "#d6efe6"];

const getSavedFilters = () => {
  try {
    return JSON.parse(localStorage.getItem("archivedBrandsData")) || {};
  } catch (e) {
    return {};
  }
};

const joinList = (value) => (Array.isArray(value) ? value.join(", ") : value || "");

const ArchivedBrands = () => {
  const dispatch = useDispatch();
  const archivedBrandsList = useSelector(
    (state) => state?.ArchivedBrands?.archivedBrandsList
  );
  const archivedBrandsLoader = useSelector(
    (state) => state?.ArchivedBrands?.archivedBrandsLoader
  );
  const archivedBrandsCount = useSelector(
    (state) => state?.ArchivedBrands?.archivedBrandsCount
  );
  const archivedBrandsSeasons = useSelector(
    (state) => state?.ArchivedBrands?.archivedBrandsSeasons
  );
  const archivedBrandStyles = useSelector(
    (state) => state?.ArchivedBrands?.archivedBrandStyles
  );
  const archivedBrandStylesLoader = useSelector(
    (state) => state?.ArchivedBrands?.archivedBrandStylesLoader
  );

  const savedFilters = getSavedFilters();
  const [searchFilter, setSearchFilter] = useState(savedFilters?.searchFilter || "");
  const [seasonFilter, setSeasonFilter] = useState(savedFilters?.seasonFilter || "ALL");
  const [openBrand, setOpenBrand] = useState(savedFilters?.openBrand ?? null);
  const [stylesPage, setStylesPage] = useState(savedFilters?.stylesPage || 1);
  const [debouncedSearch, setDebouncedSearch] = useState(searchFilter.trim());

  const seasonParam = seasonFilter === "ALL" ? "" : seasonFilter;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchFilter.trim()), 500);
    return () => clearTimeout(timer);
  }, [searchFilter]);

  useEffect(() => {
    dispatch(getArchivedBrandsList(seasonParam, debouncedSearch));
  }, [seasonParam, debouncedSearch]);

  useEffect(() => {
    if (openBrand) {
      dispatch(
        getArchivedBrandStyles(
          openBrand,
          seasonParam,
          debouncedSearch,
          stylesPage,
          stylesPageSize
        )
      );
    }
  }, [openBrand, seasonParam, debouncedSearch, stylesPage]);

  useEffect(() => {
    localStorage.setItem(
      "archivedBrandsData",
      JSON.stringify({ searchFilter, seasonFilter, openBrand, stylesPage })
    );
  }, [searchFilter, seasonFilter, openBrand, stylesPage]);

  const filteredBrands = useMemo(
    () =>
      (archivedBrandsList || []).map((item, index) => ({
        ...item,
        id: item?.brand,
        chip: chipColors[index % chipColors.length],
        seasons: item?.seasons || [],
      })),
    [archivedBrandsList]
  );

  const seasonOptions = useMemo(
    () => [
      { value: "ALL", label: "All seasons" },
      ...(archivedBrandsSeasons || []).map((season) => ({ value: season, label: season })),
    ],
    [archivedBrandsSeasons]
  );

  // ignore a styles response that belongs to a previously opened brand
  const openBrandStyles =
    archivedBrandStyles?.brand === openBrand ? archivedBrandStyles?.results || [] : [];

  const onToggleBrand = (id) => {
    setOpenBrand(openBrand === id ? null : id);
    setStylesPage(1);
  };

  const onSearchChange = (e) => {
    setSearchFilter(e.target.value);
    setStylesPage(1);
  };

  const onSeasonChange = (value) => {
    setSeasonFilter(value || "ALL");
    setStylesPage(1);
  };

  const onRefresh = () => {
    const filtersAlreadyClear = !searchFilter && !debouncedSearch && seasonFilter === "ALL";
    setSearchFilter("");
    setDebouncedSearch("");
    setSeasonFilter("ALL");
    setOpenBrand(null);
    setStylesPage(1);
    // effects only refetch when a filter changes, so reload explicitly when nothing changed
    if (filtersAlreadyClear) {
      dispatch(getArchivedBrandsList("", ""));
    }
  };

  return (
    <div className="archived-brands-wrapper">
      {archivedBrandsLoader || archivedBrandStylesLoader ? <Loader /> : null}
      <div className="archived-brands-top-wrapper">
        <div>
          <p className="archived-brands-title">Archived Brands</p>
          <p className="archived-brands-subtitle">
            {archivedBrandsCount} brands archived
          </p>
        </div>
        <div className="archived-brands-filters">
          <div className="archived-brands-sync-icon">
            <SyncOutlined spin={archivedBrandsLoader} onClick={onRefresh} />
          </div>
          <SearchInput
            className="archived-brands-search"
            placeholder="Search brand or parent style"
            value={searchFilter}
            onChange={onSearchChange}
          />
          <DropDown
            className="archived-brands-season"
            data={seasonOptions}
            value={seasonFilter}
            onChange={onSeasonChange}
          />
        </div>
      </div>

      <div className="archived-brands-list">
        {filteredBrands.map((b) => {
          const isOpen = openBrand === b.id;
          return (
            <div className="archived-brand-card" key={b.id}>
              <button
                type="button"
                className="archived-brand-header"
                aria-expanded={isOpen}
                onClick={() => onToggleBrand(b.id)}
              >
                <span className="archived-brand-chip" style={{ background: b.chip }}>
                  {b.brand}
                </span>
                <span className="archived-brand-meta">
                  <span className="archived-brand-meta-item">
                    <span className="archived-label">Styles</span>
                    <span className="archived-brand-meta-value">{b.style_count}</span>
                  </span>
                  <span className="archived-brand-meta-item">
                    <span className="archived-label">Seasons</span>
                    <span className="archived-brand-meta-value">{b.seasons.join(", ")}</span>
                  </span>
                </span>
                <span className="archived-badge">Archived</span>
                <DownOutlined
                  className={`archived-brand-arrow ${isOpen ? "archived-brand-arrow-open" : ""}`}
                />
              </button>

              {isOpen ? (
                <div className="archived-styles-container">
                  {!archivedBrandStylesLoader && openBrandStyles.length === 0 ? (
                    <div className="archived-brands-empty">No styles to display.</div>
                  ) : null}
                  {openBrandStyles.map((st, index) => {
                    const vendors = Array.isArray(st?.vendors)
                      ? st.vendors
                      : (st?.vendors || "").split(",").filter(Boolean);
                    return (
                      <div className="archived-style-row" key={`${st?.parent_style}-${index}`}>
                        <span className="archived-style-season">{st?.season}</span>
                        <span className="archived-style-col archived-style-code">
                          <span className="archived-label">Parent Style</span>
                           
                          <span className="archived-style-value-bold">
                            {st?.TechpackCreation ? (
                              <a
                                className="archived-style-link"
                                href={st?.TechpackCreation}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {st?.parent_style}
                              </a>
                            ) : (
                              st?.parent_style
                            )}
                          </span>
                        </span>
                        <span className="archived-style-col archived-style-desc">
                          <span className="archived-label">Style Description</span>
                          <span className="archived-style-value">{st?.description}</span>
                        </span>
                        <span className="archived-style-col archived-style-vendors">
                          <span className="archived-label">Vendors ({vendors.length})</span>
                          <span className="archived-style-value">{joinList(vendors)}</span>
                        </span>
                        <span className="archived-style-col archived-style-colors">
                          <span className="archived-label">Colors</span>
                          <span className="archived-style-value">{joinList(st?.colors)}</span>
                        </span>
                        <span className="archived-style-col archived-style-sizes">
                          <span className="archived-label">Sizes</span>
                          <span className="archived-style-value">{joinList(st?.sizes)}</span>
                        </span>
                      </div>
                    );
                  })}
                  {openBrandStyles.length > 0 && archivedBrandStyles?.count > stylesPageSize ? (
                    <Pagination
                      current={stylesPage}
                      total={archivedBrandStyles?.count || 0}
                      pageSize={stylesPageSize}
                      showSizeChanger={false}
                      onChange={(page) => setStylesPage(page)}
                      style={{ textAlign: "right", marginTop: "10px" }}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}

        {!archivedBrandsLoader && filteredBrands.length === 0 ? (
          <div className="archived-brands-empty">
            No archived brands match your search.
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ArchivedBrands;
